import React from "react";
import { BookOpen, ExternalLink, Loader2 } from "lucide-react";
import { passageUrl, useBibleVersion } from "@/lib/bible";
import { HighlightButton } from "./Highlights";

export const RetellingBadge = ({ refText, testId = "retelling-badge" }: { refText?: string; testId?: string }) => (
  <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded bg-amber-100/70 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-amber-900">
    <BookOpen className="h-3 w-3" /> Retelling{refText ? ` · see ${refText}` : ""}
  </span>
);

export const ScriptureRef = ({ refText, testId, plain }: { refText: string; testId?: string; plain?: boolean }) => {
  const { id, abbr } = useBibleVersion();
  return plain ? (
  <span data-testid={testId || "scripture-ref-plain"} className="inline-flex items-center rounded-full border border-amber-300/70 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-900">{refText} <span className="ml-1 font-normal text-amber-700" data-testid="scripture-version">{abbr}</span></span>
) : (
  <a href={passageUrl(refText, id)} target="_blank" rel="noreferrer" data-testid={testId || `scripture-link-${refText.replace(/\W+/g, "-").toLowerCase()}`}
    className="inline-flex items-center gap-1 rounded-full border border-amber-300/70 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-900 transition-colors hover:bg-amber-100">
    {refText} <span className="font-normal text-amber-700" data-testid="scripture-version">{abbr}</span> <ExternalLink className="h-3 w-3" />
  </a>
);
};

export const ScriptureQuote = ({ text, refText, large, plain, save }: { text: string; refText: string; large?: boolean; plain?: boolean; save?: { title: string; link: string } }) => (
  <blockquote data-testid="scripture-quote" className={`rounded-lg border-l-4 border-amber-600 bg-amber-50/70 p-4 font-scripture italic leading-loose text-amber-950/90 ${large ? "text-xl sm:text-2xl" : "text-lg"}`}>
    “{text}”
    <footer className="mt-2 flex flex-wrap items-center gap-2 not-italic"><ScriptureRef refText={refText} plain={plain} />{save && <HighlightButton refText={refText} text={text} title={save.title} link={save.link} kids={plain} />}</footer>
  </blockquote>
);

export const PageHeader = ({ metaphor, label, title, subtitle, children }: { metaphor: string; label: string; title: string; subtitle?: string; children?: React.ReactNode }) => (
  <div className="mb-10 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
    <div className="max-w-2xl">
      <p className="eyebrow" data-testid="page-eyebrow">{metaphor} · {label}</p>
      <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">{title}</h1>
      {subtitle && <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">{subtitle}</p>}
    </div>
    {children}
  </div>
);

export const Loader = ({ label = "Loading" }: { label?: string }) => (
  <div className="flex items-center gap-2 py-16 text-slate-500" data-testid="loader"><Loader2 className="h-4 w-4 animate-spin" /> {label}…</div>
);

export const FullLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-[#FAF7F2]"><Loader /></div>
);

export const Empty = ({ title, body, testId }: { title: string; body?: string; testId?: string }) => (
  <div data-testid={testId || "empty-state"} className="rounded-2xl border border-dashed border-[#E2DBCE] bg-[#F7F1E8]/95 px-8 py-12 text-center backdrop-blur-sm">
    <p className="font-display text-2xl text-slate-800">{title}</p>
    {body && <p className="mt-2 text-sm text-slate-600">{body}</p>}
  </div>
);
